import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Copy, RefreshCw, XCircle, Clock, CheckCircle, Phone, Search, X } from 'react-feather';
import { virtualNumbersAPI } from '../api';
import { useAuth } from '../context/AuthContext';
import { formatCurrencyWithConversion } from '../utils/currency';
import './VirtualNumbers.css';

// ── Helpers ──────────────────────────────────────────────────────────────────

const SERVICE_ICONS = {
  whatsapp:   { label: 'WA', color: '#25D366', textColor: '#ffffff', background: 'linear-gradient(135deg, #34d399 0%, #16a34a 100%)' },
  telegram:   { label: 'TG', color: '#0088cc', textColor: '#ffffff', background: 'linear-gradient(135deg, #38bdf8 0%, #0284c7 100%)' },
  google:     { label: 'G',  color: '#4285F4', textColor: '#ffffff', background: 'linear-gradient(135deg, #4285F4 0%, #DB4437 52%, #F4B400 100%)' },
  facebook:   { label: 'FB', color: '#1877F2', textColor: '#ffffff', background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)' },
  instagram:  { label: 'IG', color: '#E4405F', textColor: '#ffffff', background: 'linear-gradient(135deg, #f9ce34 0%, #ee2a7b 50%, #6228d7 100%)' },
  tiktok:     { label: 'TT', color: '#111827', textColor: '#ffffff', background: 'linear-gradient(135deg, #111827 0%, #0f172a 100%)' },
  twitter:    { label: 'X',  color: '#111827', textColor: '#ffffff', background: 'linear-gradient(135deg, #111827 0%, #1f2937 100%)' },
  discord:    { label: 'DC', color: '#5865F2', textColor: '#ffffff', background: 'linear-gradient(135deg, #818cf8 0%, #4f46e5 100%)' },
  snapchat:   { label: 'SC', color: '#FFFC00', textColor: '#111827', background: 'linear-gradient(135deg, #fef08a 0%, #facc15 100%)' },
  netflix:    { label: 'N',  color: '#E50914', textColor: '#ffffff', background: 'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)' },
  microsoft:  { label: 'MS', color: '#00A4EF', textColor: '#ffffff', background: 'linear-gradient(135deg, #22c55e 0%, #3b82f6 50%, #f97316 100%)' },
  apple:      { label: 'AP', color: '#555555', textColor: '#ffffff', background: 'linear-gradient(135deg, #6b7280 0%, #111827 100%)' },
  amazon:     { label: 'AZ', color: '#FF9900', textColor: '#111827', background: 'linear-gradient(135deg, #fbbf24 0%, #f97316 100%)' },
  uber:       { label: 'UB', color: '#111827', textColor: '#ffffff', background: 'linear-gradient(135deg, #0f172a 0%, #334155 100%)' },
  spotify:    { label: 'SP', color: '#1DB954', textColor: '#ffffff', background: 'linear-gradient(135deg, #22c55e 0%, #15803d 100%)' },
  linkedin:   { label: 'IN', color: '#0A66C2', textColor: '#ffffff', background: 'linear-gradient(135deg, #38bdf8 0%, #1d4ed8 100%)' },
  tinder:     { label: 'TD', color: '#FE3C72', textColor: '#ffffff', background: 'linear-gradient(135deg, #fb7185 0%, #f43f5e 50%, #f97316 100%)' },
  paypal:     { label: 'PP', color: '#003087', textColor: '#ffffff', background: 'linear-gradient(135deg, #2563eb 0%, #1e3a8a 100%)' },
};

function getServiceStyle(name = '') {
  const key = name.toLowerCase();
  for (const [k, v] of Object.entries(SERVICE_ICONS)) {
    if (key.includes(k)) return v;
  }
  return {
    label: getServiceLabel(name),
    color: '#6366F1',
    textColor: '#ffffff',
    background: 'linear-gradient(135deg, #818cf8 0%, #4f46e5 100%)',
  };
}

function getServiceLabel(name = '') {
  const words = String(name)
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (words.length === 0) return 'VN';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return `${words[0][0] || ''}${words[1][0] || ''}`.toUpperCase();
}

const FLAG_MAP = {
  'argentina':'🇦🇷','australia':'🇦🇺','brazil':'🇧🇷','cameroon':'🇨🇲',
  'united states':'🇺🇸','usa':'🇺🇸','united kingdom':'🇬🇧','uk':'🇬🇧',
  'canada':'🇨🇦','france':'🇫🇷','germany':'🇩🇪','india':'🇮🇳',
  'russia':'🇷🇺','china':'🇨🇳','japan':'🇯🇵','spain':'🇪🇸',
  'italy':'🇮🇹','mexico':'🇲🇽','indonesia':'🇮🇩','turkey':'🇹🇷',
  'vietnam':'🇻🇳','thailand':'🇹🇭','philippines':'🇵🇭','egypt':'🇪🇬',
  'south africa':'🇿🇦','nigeria':'🇳🇬','colombia':'🇨🇴','poland':'🇵🇱',
  'ukraine':'🇺🇦','malaysia':'🇲🇾','kenya':'🇰🇪','ghana':'🇬🇭',
  'morocco':'🇲🇦','pakistan':'🇵🇰','bangladesh':'🇧🇩','sri lanka':'🇱🇰',
};
function getFlag(name = '') { return FLAG_MAP[name.toLowerCase()] || '🌍'; }

// ── Component ─────────────────────────────────────────────────────────────────

const TABS = [
  { id: 'services',   label: 'Services' },
  { id: 'countries',  label: 'Countries' },
  { id: 'rent',       label: 'Rent Number' },
  { id: 'orders',     label: 'Orders' },
  { id: 'codes',      label: 'Codes' },
];

function dedupeServices(list = []) {
  const seen = new Set();
  return list.filter((service) => {
    const key = `${String(service.code || '').toLowerCase()}::${String(service.name || '').toLowerCase()}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export default function VirtualNumbers() {
  const { user, refreshUser } = useAuth();

  const [tab, setTab]                 = useState('services');
  const [search, setSearch]           = useState('');

  // Data
  const [services, setServices]       = useState([]);
  const [countries, setCountries]     = useState([]);
  const [orders, setOrders]           = useState([]);
  const [prices, setPrices]           = useState([]);
  const [rentalCountries, setRentalCountries] = useState([]);
  const [rentalOrders, setRentalOrders] = useState([]);

  // Selection
  const [selectedService, setSelectedService] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedPrice,   setSelectedPrice]   = useState(null);
  const [selectedRentalCountry, setSelectedRentalCountry] = useState(null);
  const [selectedRentalTerm, setSelectedRentalTerm] = useState(null);

  // UI state
  const [loading, setLoading]         = useState(true);
  const [loadingPrices, setLoadingPrices] = useState(false);
  const [purchasing, setPurchasing]   = useState(false);
  const [checking,   setChecking]     = useState(null);   // orderId being checked
  const [canceling,  setCanceling]    = useState(null);   // orderId being cancelled
  const [cancelingRental, setCancelingRental] = useState(null);
  const [copied,     setCopied]       = useState(null);
  const [msg, setMsg]                 = useState({ text: '', type: '' });
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);

  const resetPurchaseFlow = useCallback(() => {
    setSelectedService(null);
    setSelectedCountry(null);
    setSelectedPrice(null);
    setSelectedRentalCountry(null);
    setSelectedRentalTerm(null);
    setPrices([]);
    setShowPurchaseModal(false);
    setSearch('');
  }, []);

  // ── Data fetching ─────────────────────────────────────────────────────────

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [svcRes, cntRes, ordRes, rentalCntRes, rentalOrdRes] = await Promise.allSettled([
        virtualNumbersAPI.getServices(),
        virtualNumbersAPI.getCountries(),
        virtualNumbersAPI.getOrders(),
        virtualNumbersAPI.getRentalCountries(),
        virtualNumbersAPI.getRentalOrders(),
      ]);
      setServices(
        svcRes.status === 'fulfilled'
          ? dedupeServices(svcRes.value.data.services || [])
          : []
      );
      setCountries(cntRes.status === 'fulfilled' ? (cntRes.value.data.countries || []) : []);
      setOrders(ordRes.status === 'fulfilled' ? (ordRes.value.data.orders || []) : []);
      setRentalCountries(rentalCntRes.status === 'fulfilled' ? (rentalCntRes.value.data.countries || []) : []);
      setRentalOrders(rentalOrdRes.status === 'fulfilled' ? (rentalOrdRes.value.data.orders || []) : []);

      if ([svcRes, cntRes, ordRes].every((result) => result.status !== 'fulfilled')) {
        setMsg({ text: 'Failed to load data. Check your API connection.', type: 'error' });
      } else if (rentalCntRes.status !== 'fulfilled' || rentalOrdRes.status !== 'fulfilled') {
        setMsg({ text: 'Rental data is temporarily unavailable, but one-time numbers are still available.', type: 'error' });
      }
    } catch (e) {
      setMsg({ text: 'Failed to load data. Check your API connection.', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Auto-refresh orders every 10 s when on Orders or Codes tab
  useEffect(() => {
    if (tab !== 'orders' && tab !== 'codes') return;
    const id = setInterval(async () => {
      try {
        const res = await virtualNumbersAPI.getOrders();
        setOrders(res.data.orders || []);
      } catch {}
    }, 10000);
    return () => clearInterval(id);
  }, [tab]);

  useEffect(() => {
    if (tab !== 'rent') return;
    const id = setInterval(async () => {
      try {
        const res = await virtualNumbersAPI.getRentalOrders();
        setRentalOrders(res.data.orders || []);
      } catch {}
    }, 10000);
    return () => clearInterval(id);
  }, [tab]);

  // ── Actions ───────────────────────────────────────────────────────────────

  const fetchPrices = async (serviceCode) => {
    setLoadingPrices(true);
    setPrices([]);
    try {
      const res = await virtualNumbersAPI.getPrices(serviceCode);
      setPrices(res.data.prices || []);
    } catch {
      setMsg({ text: 'Failed to fetch prices for this service', type: 'error' });
    } finally {
      setLoadingPrices(false);
    }
  };

  const handleSelectService = (svc) => {
    setSelectedService(svc);
    setSelectedCountry(null);
    setSelectedPrice(null);
    setSearch('');
    fetchPrices(svc.code);
  };

  const handleSelectCountry = async (country) => {
    setSelectedCountry(country);
    setSelectedService(null);
    setSelectedPrice(null);
    setSearch('');
    setLoadingPrices(true);
    setPrices([]);
    // Load all services then filter by country in prices
    // We'll just show a service picker for the country
    setLoadingPrices(false);
  };

  const handleSelectPriceRow = (priceItem) => {
    setSelectedPrice(priceItem);
    setShowPurchaseModal(true);
  };

  const handleSelectRentalCountry = (country) => {
    setSelectedRentalCountry(country);
    setSelectedRentalTerm(null);
    setSearch('');
  };

  const handlePurchase = async () => {
    if (!selectedService || !selectedPrice) return;
    setPurchasing(true);
    setMsg({ text: '', type: '' });
    try {
      await virtualNumbersAPI.purchaseNumber(
        selectedPrice.countryId,
        selectedService.code,
        selectedPrice.countryName,
        selectedService.name
      );
      setMsg({ text: '✅ Number purchased! Check Orders tab.', type: 'success' });
      setShowPurchaseModal(false);
      setSelectedPrice(null);
      setSelectedService(null);
      setSelectedCountry(null);
      setPrices([]);
      await refreshUser();
      const res = await virtualNumbersAPI.getOrders();
      setOrders(res.data.orders || []);
      setTab('orders');
    } catch (e) {
      setMsg({ text: e.response?.data?.error || 'Purchase failed', type: 'error' });
    } finally {
      setPurchasing(false);
    }
  };

  const handlePurchaseRental = async () => {
    if (!selectedRentalCountry || !selectedRentalTerm) return;
    setPurchasing(true);
    setMsg({ text: '', type: '' });
    try {
      await virtualNumbersAPI.purchaseRentalNumber(
        selectedRentalCountry.id,
        selectedRentalCountry.name,
        selectedRentalTerm
      );
      setMsg({ text: 'Rental number purchased! Check the Rent Number tab for your active rental.', type: 'success' });
      setSelectedRentalCountry(null);
      setSelectedRentalTerm(null);
      setSearch('');
      await refreshUser();
      const res = await virtualNumbersAPI.getRentalOrders();
      setRentalOrders(res.data.orders || []);
    } catch (e) {
      setMsg({ text: e.response?.data?.error || 'Rental purchase failed', type: 'error' });
    } finally {
      setPurchasing(false);
    }
  };

  const handleCheckSMS = async (orderId) => {
    setChecking(orderId);
    try {
      await virtualNumbersAPI.checkOrder(orderId);
      const res = await virtualNumbersAPI.getOrders();
      setOrders(res.data.orders || []);
    } catch {}
    setChecking(null);
  };

  const handleCancel = async (orderId) => {
    setCanceling(orderId);
    try {
      await virtualNumbersAPI.cancelOrder(orderId);
      await refreshUser();
      const res = await virtualNumbersAPI.getOrders();
      setOrders(res.data.orders || []);
      setMsg({ text: 'Order cancelled and refunded.', type: 'success' });
    } catch (e) {
      setMsg({ text: e.response?.data?.error || 'Cancel failed', type: 'error' });
    }
    setCanceling(null);
  };

  const handleCancelRental = async (rentalId) => {
    setCancelingRental(rentalId);
    try {
      await virtualNumbersAPI.cancelRentalOrder(rentalId);
      await refreshUser();
      const res = await virtualNumbersAPI.getRentalOrders();
      setRentalOrders(res.data.orders || []);
      setMsg({ text: 'Rental order cancelled and refunded.', type: 'success' });
    } catch (e) {
      setMsg({ text: e.response?.data?.error || 'Rental cancel failed', type: 'error' });
    }
    setCancelingRental(null);
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text).catch(() => {});
    setCopied(text);
    setTimeout(() => setCopied(null), 2000);
  };

  const switchTab = (id) => {
    setTab(id);
    setSearch('');
    if (id !== 'services' && id !== 'countries') {
      resetPurchaseFlow();
    }
  };

  // ── Filtered lists ────────────────────────────────────────────────────────

  const filteredServices = useMemo(() =>
    services.filter(s => s.name.toLowerCase().includes(search.toLowerCase())),
    [services, search]);

  const filteredCountries = useMemo(() =>
    countries.filter(c => c.name.toLowerCase().includes(search.toLowerCase())),
    [countries, search]);

  const filteredRentPrices = useMemo(() =>
    prices.filter(p => p.countryName.toLowerCase().includes(search.toLowerCase())),
    [prices, search]);

  const filteredRentServices = useMemo(() =>
    services.filter(s => s.name.toLowerCase().includes(search.toLowerCase())),
    [services, search]);

  const filteredRentalCountries = useMemo(() =>
    rentalCountries.filter(c => c.name.toLowerCase().includes(search.toLowerCase())),
    [rentalCountries, search]);

  const activeOrders    = orders.filter(o => o.status === 'pending');
  const completedOrders = orders.filter(o => o.status === 'completed');
  const activeRentalOrders = rentalOrders.filter(o => o.status === 'active');
  const showTopSearch =
    (tab === 'services' && !selectedService) ||
    (tab === 'countries' && !selectedCountry) ||
    (tab === 'rent' && !selectedRentalCountry);

  // ── Render ────────────────────────────────────────────────────────────────

  const renderServices = () => {
    if (loading) return <div className="vn-center"><div className="spinner" /></div>;

    if (selectedService) {
      return (
        <div>
          <button
            className="vn-back-btn"
            onClick={() => {
              setSelectedService(null);
              setSelectedPrice(null);
              setPrices([]);
              setShowPurchaseModal(false);
              setSearch('');
            }}
          >
            ‹ Back to Services
          </button>
          <div className="vn-rent-service-header">
            <div
              className="vn-row-icon vn-service-badge"
              style={{
                background: getServiceStyle(selectedService.name).background || getServiceStyle(selectedService.name).color,
                color: getServiceStyle(selectedService.name).textColor,
                boxShadow: `0 10px 24px ${getServiceStyle(selectedService.name).color}33`,
                width: 48,
                height: 48,
                borderRadius: 14,
              }}
            >
              {getServiceStyle(selectedService.name).label}
            </div>
            <div>
              <div className="vn-rent-title">{selectedService.name}</div>
              <div className="vn-rent-sub">Select a country to continue your purchase</div>
            </div>
          </div>

          <div className="vn-search vn-rent-search">
            <Search size={16} color="var(--text-muted)" className="vn-search-icon" />
            <input
              type="text"
              placeholder="Search countries..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          {loadingPrices ? (
            <div className="vn-center"><div className="spinner" /></div>
          ) : filteredRentPrices.length === 0 ? (
            <div className="vn-empty">No countries available for this service right now.</div>
          ) : (
            <div className="vn-price-list">
              {filteredRentPrices.map((p, i) => (
                <button
                  key={`${p.countryId}-${i}`}
                  className={`vn-price-row ${selectedPrice?.countryId === p.countryId ? 'selected' : ''}`}
                  onClick={() => handleSelectPriceRow(p)}
                >
                  <div className="vn-price-left">
                    <span className="vn-price-flag">{getFlag(p.countryName)}</span>
                    <div>
                      <div className="vn-price-country">{p.countryName}</div>
                      <div className="vn-price-rate">Success Rate: {p.successRate}%</div>
                    </div>
                  </div>
                  <div className="vn-price-right">
                    <span className="vn-price-amount">{formatCurrencyWithConversion(p.price, user?.currency, 2)}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (!filteredServices.length) return <div className="vn-empty">No services found.</div>;
    return (
      <div className="vn-list">
        {filteredServices.map(svc => {
          const style = getServiceStyle(svc.name);
          return (
            <button key={svc.code} className="vn-row" onClick={() => handleSelectService(svc)}>
              <div
                className="vn-row-icon vn-service-badge"
                style={{
                  background: style.background || style.color,
                  color: style.textColor,
                  boxShadow: `0 10px 24px ${style.color}26`,
                }}
              >
                <span>{style.label}</span>
              </div>
              <span className="vn-row-name">{svc.name}</span>
              <span className="vn-row-chevron">›</span>
            </button>
          );
        })}
      </div>
    );
  };

  const renderCountries = () => {
    if (loading) return <div className="vn-center"><div className="spinner" /></div>;

    if (selectedCountry) {
      return (
        <div>
          <button
            className="vn-back-btn"
            onClick={() => {
              setSelectedCountry(null);
              setSelectedService(null);
              setSelectedPrice(null);
              setShowPurchaseModal(false);
              setSearch('');
            }}
          >
            ‹ Back to Countries
          </button>
          <div className="vn-rent-service-header">
            <span style={{ fontSize: 40 }}>{getFlag(selectedCountry.name)}</span>
            <div>
              <div className="vn-rent-title">{selectedCountry.name}</div>
              <div className="vn-rent-sub">Select a service to continue your purchase</div>
            </div>
          </div>

          <div className="vn-search vn-rent-search">
            <Search size={16} color="var(--text-muted)" className="vn-search-icon" />
            <input
              type="text"
              placeholder="Search services..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          <div className="vn-list">
            {filteredRentServices.map(svc => {
              const style = getServiceStyle(svc.name);
              return (
                <button key={svc.code} className="vn-row" onClick={async () => {
                  setSelectedService(svc);
                  setLoadingPrices(true);
                  setPrices([]);
                  try {
                    const res = await virtualNumbersAPI.getPrices(svc.code);
                    const allPrices = res.data.prices || [];
                    const match = allPrices.find(p =>
                      String(p.countryId) === String(selectedCountry.id) ||
                      p.countryName.toLowerCase() === selectedCountry.name.toLowerCase()
                    );
                    if (match) {
                      setPrices(allPrices);
                      setSelectedPrice({ ...match, countryId: selectedCountry.id, countryName: selectedCountry.name });
                      setShowPurchaseModal(true);
                    } else {
                      setMsg({ text: `${svc.name} is not available in ${selectedCountry.name}.`, type: 'error' });
                      setSelectedService(null);
                    }
                  } catch {
                    setMsg({ text: 'Failed to check service availability', type: 'error' });
                  } finally {
                    setLoadingPrices(false);
                  }
                }}>
                  <div
                    className="vn-row-icon vn-service-badge"
                    style={{
                      background: style.background || style.color,
                      color: style.textColor,
                      boxShadow: `0 10px 24px ${style.color}26`,
                    }}
                  >
                    {style.label}
                  </div>
                  <span className="vn-row-name">{svc.name}</span>
                  <span className="vn-row-chevron">›</span>
                </button>
              );
            })}
          </div>
        </div>
      );
    }

    if (!filteredCountries.length) return <div className="vn-empty">No countries found.</div>;
    return (
      <div className="vn-list">
        {filteredCountries.map(c => (
          <button key={c.id} className="vn-row" onClick={() => handleSelectCountry(c)}>
            <div className="vn-row-icon vn-flag-icon">
              <span style={{ fontSize: 22 }}>{getFlag(c.name)}</span>
            </div>
            <div className="vn-row-info">
              <span className="vn-row-name">{c.name}</span>
              <span className="vn-row-sub">Tap to see services</span>
            </div>
            <span className="vn-row-chevron">›</span>
          </button>
        ))}
      </div>
    );
  };

  const renderRent = () => {
    if (loading) return <div className="vn-center"><div className="spinner" /></div>;

    if (selectedRentalCountry) {
      const rentalTerms = selectedRentalCountry.terms?.length ? selectedRentalCountry.terms : [];

      return (
        <div>
          <button
            className="vn-back-btn"
            onClick={() => {
              setSelectedRentalCountry(null);
              setSelectedRentalTerm(null);
              setSearch('');
            }}
          >
            ‹ Back to Rental Countries
          </button>

          <div className="vn-rent-service-header">
            <span style={{ fontSize: 40 }}>{getFlag(selectedRentalCountry.name)}</span>
            <div>
              <div className="vn-rent-title">{selectedRentalCountry.name}</div>
              <div className="vn-rent-sub">
                Choose how many days you want to keep this rental number
              </div>
            </div>
          </div>

          {rentalTerms.length === 0 ? (
            <div className="vn-empty">No rental terms are available for this country right now.</div>
          ) : (
            <>
              <div className="vn-rental-terms">
                {rentalTerms.map((term) => (
                  <button
                    key={term}
                    className={`vn-rental-term-btn ${selectedRentalTerm === term ? 'active' : ''}`}
                    onClick={() => setSelectedRentalTerm(term)}
                  >
                    <span className="vn-rental-term-days">{term}</span>
                    <span className="vn-rental-term-label">days</span>
                  </button>
                ))}
              </div>

              <div className="vn-rental-purchase-card">
                <div className="vn-rental-purchase-row">
                  <span>Country</span>
                  <strong>{getFlag(selectedRentalCountry.name)} {selectedRentalCountry.name}</strong>
                </div>
                <div className="vn-rental-purchase-row">
                  <span>Region</span>
                  <strong>{selectedRentalCountry.region || 'Unknown'}</strong>
                </div>
                <div className="vn-rental-purchase-row">
                  <span>Term</span>
                  <strong>{selectedRentalTerm ? `${selectedRentalTerm} days` : 'Choose a term'}</strong>
                </div>
                <p className="vn-modal-note" style={{ textAlign: 'left', marginTop: 14 }}>
                  Rental pricing comes directly from SMSPool at purchase time, then your NiceBooster markup is applied.
                </p>
                <button
                  className="btn btn-primary vn-modal-action"
                  onClick={handlePurchaseRental}
                  disabled={!selectedRentalTerm || purchasing}
                >
                  {purchasing ? 'Purchasing…' : 'Purchase Rental Number'}
                </button>
              </div>
            </>
          )}
        </div>
      );
    }

    return (
      <div className="vn-rental-layout">
        <div>
          <div className="vn-rental-header-card">
            <div>
              <div className="vn-rent-title">Long-term Rental Numbers</div>
              <div className="vn-rent-sub">Choose a supported country and keep the number for multiple days.</div>
            </div>
            <div className="vn-rental-count">{filteredRentalCountries.length}</div>
          </div>

          {filteredRentalCountries.length === 0 ? (
            <div className="vn-empty">No rental countries found.</div>
          ) : (
            <div className="vn-list">
              {filteredRentalCountries.map((country) => (
                <button key={country.id} className="vn-row" onClick={() => handleSelectRentalCountry(country)}>
                  <div className="vn-row-icon vn-flag-icon">
                    <span style={{ fontSize: 22 }}>{getFlag(country.name)}</span>
                  </div>
                  <div className="vn-row-info">
                    <span className="vn-row-name">{country.name}</span>
                    <span className="vn-row-sub">
                      {country.region || 'Unknown region'} · Terms: {(country.terms || []).join(', ') || 'N/A'} days
                    </span>
                  </div>
                  <span className="vn-row-chevron">›</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="vn-rental-orders-panel">
          <div className="vn-rental-orders-title">Active Rentals</div>
          {activeRentalOrders.length === 0 ? (
            <div className="vn-empty vn-rental-orders-empty">
              <Clock size={34} color="var(--text-muted)" />
              <p>No active rental numbers yet.</p>
            </div>
          ) : (
            <div className="vn-order-list">
              {activeRentalOrders.map((order) => (
                <div key={order.id} className="vn-order-card">
                  <div className="vn-order-top">
                    <div className="vn-order-svc">
                      <span className="vn-order-icon" style={{ background: 'rgba(59,130,246,.12)' }}>{getFlag(order.country_name)}</span>
                      <div>
                        <div className="vn-order-name">{order.country_name}</div>
                        <div className="vn-order-country">{order.term_days} day rental{order.pool_name ? ` · ${order.pool_name}` : ''}</div>
                      </div>
                    </div>
                    <span className="badge badge-active">ACTIVE</span>
                  </div>

                  <div className="vn-order-number-box">
                    <Phone size={16} color="var(--primary)" />
                    <span className="vn-phone">+{order.phone_number}</span>
                    <button className="vn-copy-btn" onClick={() => handleCopy(order.phone_number)}>
                      {copied === order.phone_number ? <CheckCircle size={14} color="var(--green-500)" /> : <Copy size={14} />}
                    </button>
                  </div>

                  <div className="vn-order-actions">
                    <button
                      className="btn btn-sm vn-cancel-btn"
                      onClick={() => handleCancelRental(order.rental_id)}
                      disabled={cancelingRental === order.rental_id}
                    >
                      <XCircle size={13} /> {cancelingRental === order.rental_id ? 'Cancelling…' : 'Cancel Rental'}
                    </button>
                  </div>

                  <div className="vn-order-cost">
                    Cost: {formatCurrencyWithConversion(order.cost, user?.currency, 2)}
                    {order.expires_at ? ` · Expires ${new Date(order.expires_at).toLocaleString()}` : ''}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderPurchaseModal = () => {
    if (!showPurchaseModal || !selectedService || !selectedPrice) return null;
    const selectedServiceStyle = getServiceStyle(selectedService.name);

    return (
      <div className="vn-modal-backdrop" onClick={() => setShowPurchaseModal(false)}>
        <div className="vn-modal-card" onClick={e => e.stopPropagation()}>
          <div className="vn-modal-head">
            <div className="vn-modal-handle" />
            <div className="vn-modal-head-row">
              <div>
                <div className="vn-modal-title">Confirm Purchase</div>
              </div>
              <button className="vn-modal-close" onClick={() => setShowPurchaseModal(false)}>
                <X size={18} />
              </button>
            </div>
          </div>

          <div className="vn-modal-summary">
            <div
              className="vn-modal-summary-icon"
              style={{
                background: selectedServiceStyle.background || selectedServiceStyle.color,
                color: selectedServiceStyle.textColor,
                boxShadow: `0 12px 28px ${selectedServiceStyle.color}2e`,
              }}
            >
              <span>{selectedServiceStyle.label}</span>
            </div>
            <div className="vn-modal-summary-copy">
              <strong>{selectedService.name}</strong>
              <span>Virtual SMS Number</span>
            </div>
          </div>

          <div className="vn-modal-details">
            <div className="vn-modal-detail">
              <span>Service</span>
              <strong>{selectedServiceStyle.label} {selectedService.name}</strong>
            </div>
            <div className="vn-modal-detail">
              <span>Country</span>
              <strong>{getFlag(selectedPrice.countryName)} {selectedPrice.countryName}</strong>
            </div>
            <div className="vn-modal-detail">
              <span>Success Rate</span>
              <strong>{selectedPrice.successRate}%</strong>
            </div>
            <div className="vn-modal-detail">
              <span>Price</span>
              <strong>{formatCurrencyWithConversion(selectedPrice.price, user?.currency, 2)}</strong>
            </div>
          </div>

          <p className="vn-modal-note">
            A number will be assigned to you instantly. You have a limited time to receive your SMS code.
          </p>

          <button className="btn btn-primary vn-modal-action" onClick={handlePurchase} disabled={purchasing}>
            {purchasing ? 'Purchasing…' : 'Purchase Number'}
          </button>
        </div>
      </div>
    );
  };

  const renderOrders = () => {
    if (loading) return <div className="vn-center"><div className="spinner" /></div>;
    if (activeOrders.length === 0) return (
      <div className="vn-empty">
        <Clock size={40} color="var(--text-muted)" />
        <p>No active numbers. <br />Rent a number from the <strong>Services</strong> tab.</p>
      </div>
    );
    return (
      <div className="vn-order-list">
        {activeOrders.map(order => {
          const style = getServiceStyle(order.service_name);
          return (
            <div key={order.id} className="vn-order-card">
              <div className="vn-order-top">
                <div className="vn-order-svc">
                  <span
                    className="vn-order-icon vn-service-badge"
                    style={{
                      background: style.background || style.color,
                      color: style.textColor,
                      boxShadow: `0 10px 24px ${style.color}26`,
                    }}
                  >
                    {style.label}
                  </span>
                  <div>
                    <div className="vn-order-name">{order.service_name}</div>
                    <div className="vn-order-country">{getFlag(order.country_name)} {order.country_name}</div>
                  </div>
                </div>
                <span className="badge badge-pending">PENDING</span>
              </div>
              <div className="vn-order-number-box">
                <Phone size={16} color="var(--primary)" />
                <span className="vn-phone">+{order.phone_number}</span>
                <button className="vn-copy-btn" onClick={() => handleCopy(order.phone_number)}>
                  {copied === order.phone_number ? <CheckCircle size={14} color="var(--green-500)" /> : <Copy size={14} />}
                </button>
              </div>
              <div className="vn-order-sms-status">
                <Clock size={14} className="spin-slow" />
                <span>Waiting for SMS code…</span>
              </div>
              <div className="vn-order-actions">
                <button className="btn btn-secondary btn-sm" onClick={() => handleCheckSMS(order.order_id)} disabled={checking === order.order_id}>
                  <RefreshCw size={13} /> {checking === order.order_id ? 'Checking…' : 'Check SMS'}
                </button>
                <button className="btn btn-sm vn-cancel-btn" onClick={() => handleCancel(order.order_id)} disabled={canceling === order.order_id}>
                  <XCircle size={13} /> {canceling === order.order_id ? 'Cancelling…' : 'Cancel'}
                </button>
              </div>
              <div className="vn-order-cost">Cost: {formatCurrencyWithConversion(order.cost, user?.currency, 2)} · {new Date(order.created_at).toLocaleString()}</div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderCodes = () => {
    if (loading) return <div className="vn-center"><div className="spinner" /></div>;
    if (completedOrders.length === 0) return (
      <div className="vn-empty">
        <CheckCircle size={40} color="var(--text-muted)" />
        <p>No SMS codes yet. Codes will appear here once received.</p>
      </div>
    );
    return (
      <div className="vn-order-list">
        {completedOrders.map(order => {
          const style = getServiceStyle(order.service_name);
          return (
            <div key={order.id} className="vn-order-card vn-order-done">
              <div className="vn-order-top">
                <div className="vn-order-svc">
                  <span
                    className="vn-order-icon vn-service-badge"
                    style={{
                      background: style.background || style.color,
                      color: style.textColor,
                      boxShadow: `0 10px 24px ${style.color}26`,
                    }}
                  >
                    {style.label}
                  </span>
                  <div>
                    <div className="vn-order-name">{order.service_name}</div>
                    <div className="vn-order-country">{getFlag(order.country_name)} {order.country_name}</div>
                  </div>
                </div>
                <span className="badge badge-active">DONE</span>
              </div>
              <div className="vn-order-number-box">
                <Phone size={16} color="var(--primary)" />
                <span className="vn-phone">+{order.phone_number}</span>
              </div>
              <div className="vn-code-box">
                <div>
                  <div className="vn-code-label">SMS Code</div>
                  <div className="vn-code-value">{order.sms_code}</div>
                  {order.sms_text && <div className="vn-code-text">{order.sms_text}</div>}
                </div>
                <button className="vn-copy-btn vn-copy-large" onClick={() => handleCopy(order.sms_code)}>
                  {copied === order.sms_code ? <CheckCircle size={16} color="var(--green-500)" /> : <Copy size={16} />}
                  {copied === order.sms_code ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <div className="vn-order-cost">Cost: {formatCurrencyWithConversion(order.cost, user?.currency, 2)} · {new Date(order.created_at).toLocaleString()}</div>
            </div>
          );
        })}
      </div>
    );
  };

  // ── Main render ───────────────────────────────────────────────────────────

  return (
    <div className="vn-page">
      <h1 className="page-title vn-page-title">Virtual Numbers</h1>
      <p className="page-subtitle">Rent temporary numbers for SMS verification</p>

      {msg.text && (
        <div className={`alert alert-${msg.type}`} style={{ marginBottom: 16 }} onClick={() => setMsg({ text: '', type: '' })}>
          {msg.text}
        </div>
      )}

      <div className="card vn-card">
        {/* ── Search bar ── */}
        {showTopSearch && (
          <div className="vn-search">
            <Search size={16} color="var(--text-muted)" className="vn-search-icon" />
            <input
              type="text"
              placeholder={
                tab === 'countries'
                  ? 'Search countries…'
                  : tab === 'rent'
                    ? selectedRentalCountry ? 'Search disabled while choosing term…' : 'Search rental countries…'
                    : 'Search services…'
              }
              value={search}
              onChange={e => setSearch(e.target.value)}
              disabled={tab === 'rent' && Boolean(selectedRentalCountry)}
            />
          </div>
        )}

        {/* ── Tab bar ── */}
        <div className="vn-tab-bar">
          {TABS.map(t => (
            <button
              key={t.id}
              className={`vn-tab-btn ${tab === t.id ? 'active' : ''}`}
              onClick={() => switchTab(t.id)}
            >
              {t.label}
              {t.id === 'orders' && activeOrders.length > 0 && (
                <span className="vn-tab-badge">{activeOrders.length}</span>
              )}
              {t.id === 'codes' && completedOrders.length > 0 && (
                <span className="vn-tab-badge vn-tab-badge-green">{completedOrders.length}</span>
              )}
            </button>
          ))}
        </div>

        {/* ── Tab content ── */}
        <div className="vn-content">
          {tab === 'services'  && renderServices()}
          {tab === 'countries' && renderCountries()}
          {tab === 'rent'      && renderRent()}
          {tab === 'orders'    && renderOrders()}
          {tab === 'codes'     && renderCodes()}
        </div>
      </div>
      {renderPurchaseModal()}
    </div>
  );
}
